import {CellBuilderIconLabel, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {ICellBuilder, IRowSortFn} from "/@back@/commons/widgets/grid-core.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {EFolderState, GridDataRowJsonTree} from "/@back@/commons/widgets/tree.js";
import {JNodeShortProps} from "/@lib@/store/urlTree.js";
import {IDepotShortResUiEnv, IDepotUiEnv} from "/@lib@/store/depot.js";
import {DepotResTypeProv, resPath} from "/@lib@/store/res.js";
import {POPUP, PopupTooltip} from "/@back@/commons/widgets/popups.js";
import {IView, VIEWS} from "/@lib@/commons/views.js";


export class CellBuilderResIconName extends CellBuilderIconLabel {

	protected resTypes: DepotResTypeProv;

	constructor(reg: IReg<IDepotUiEnv>, public noTooltip?: boolean) {
		super(null);
		this.resTypes = reg.env.resTypes;
		this.iconFilter = "var(--filter)";
	}

	protected _getValue(row: GridDataRowJson<JNodeShortProps>): string {
		return row.cacheHolder['resNm'] || (row.cacheHolder['resNm'] = this.resTypes.getResTypeFor(row.rowDatas).resName(row.rowDatas));
	}

	protected _getIcon(row: GridDataRowJson<JNodeShortProps>): string {
		if (row instanceof GridDataRowJsonTree && row.getFolderState() === EFolderState.opened) return row.cacheHolder['iconOpen'] || (row.cacheHolder['iconOpen'] = this.resTypes.getResTypeFor(row.rowDatas).resIcon(row.rowDatas, 'opened'));
		return row.cacheHolder['icon'] || (row.cacheHolder['icon'] = this.resTypes.getResTypeFor(row.rowDatas).resIcon(row.rowDatas));
	}

	redrawCell(row: GridDataRowJson<any>, root: HTMLElement): void {
		(root as JNodePropsPointer)._nodeProps = row.rowDatas;
		//if( isTooltipPointer(root)) root.popupTooltip.contentRoot
		super.redrawCell(row, root);
	}

	protected _buildContent(row: GridDataRowJson<JNodeShortProps>, root: HTMLElement): HTMLElement {
		if (!this.noTooltip) this._addTooltip(root);
		return super._buildContent(row, root);
	}

	protected _addTooltip(root: HTMLElement & JNodePropsPointer) {
		//On remonte sur la cell pour que le positionnement du tooltip reste dans la zone du grid.
		const cell = DOM.findParentOrSelf(root, null, (n): n is HTMLElement => n instanceof Element && n.classList.contains("cell")) || root;
		// TODO tooltip
		POPUP.promiseTooltip(cell,
			(owner: HTMLElement, tooltip: PopupTooltip): IView | null => {
				tooltip.addEventListener('c-show', async function (this: PopupTooltip, ev: CustomEvent) {
					const reg = REG.findReg<IDepotShortResUiEnv>(this.tooltipOf);
					const nodeProps = root._nodeProps;
					if (nodeProps) {
						const resType = reg.env.resTypes.getResTypeFor(nodeProps);
						const area = resType.shortResView("hover");
						if (area) {
							const resReg = resType.newShortResRegFromDepotReg(reg, nodeProps);
							const body = await area.loadBody(resReg);
							this.view.textContent = null;
							this.view.appendChild(body);
							VIEWS.onViewShown(body);
							return;
						}
					}
					//Pas dispo
					ev.preventDefault();
				});
				tooltip.addEventListener('c-close', async function (this: PopupTooltip, ev: CustomEvent) {
					const view = this.view?.firstElementChild as IView;
					if (view) {
						VIEWS.onViewHidden(view, true);
						view.textContent = null;
					}
				});
				return <div style="min-width:min(15em, 30vw)"/>;
			},
			{
				hoverAllowed: true,
				anchor: {
					posFrom: root, intersectWith: cell, fromX: 'end', marginX: -3,
					notAvailableSpace: {
						posFrom: root, intersectWith: cell, fromX: 'start', targetX: 'end', marginX: 3
					}
				}
			}
		);
	}

	getColSortFn(): IRowSortFn | null {
		//TODO à construire à partir de UrlTreeSrv.naturalSortPathFn
		return null;
	}
}

export class CellBuilderResTile implements ICellBuilder {

	protected reg: IReg<IDepotUiEnv>;
	protected resTypes: DepotResTypeProv;

	constructor(reg: IReg<IDepotUiEnv>) {
		this.reg = reg;
		this.resTypes = reg.env.resTypes;
	}

	redrawCell(row: GridDataRowJson<JNodeShortProps>, root: HTMLElement): void {
		if (!root.firstChild) {
			root.append(<div class="preview"/>);
			root.append(<div class="name"/>);
		}
		const node = row.rowKey;
		const resType = this.resTypes.getResTypeFor(node);
		const previewArea = resType.shortResView("preview");
		const previewRoot = root.firstElementChild as HTMLElement;
		previewRoot.textContent = null;
		if (previewArea) {
			const ctx = resType.newShortResRegFromDepotReg(this.reg, node);
			function drawTag(tag:HTMLElement) {
				if (tag) {
					tag.style.pointerEvents = "none"; //utile en particulier pour les ResFrameView (iframe)
					previewRoot.appendChild(tag);
				} else {
					//previewRoot.appendChild(<img src={resType.resIcon(node)}/>);
					previewRoot.style.backgroundImage = `url(${resType.resIcon(node)})`;
				}
			}
			if (previewArea.needAsync(ctx)) {
				previewRoot.style.backgroundImage = "";
				previewArea.loadBody(ctx).then(drawTag);
			} else {
				drawTag(previewArea.buildBody(ctx))
			}
		} else {
			//previewRoot.appendChild(<img src={resType.resIcon(node)}/>);
			previewRoot.style.backgroundImage = `url(${resType.resIcon(node)})`;
		}
		root.title = root.lastElementChild.textContent = resType.resName(node);
	}

	getColSortFn(): IRowSortFn | null {
		return null;
	}
}


REG.reg.registerSkin('store-res-tile', 1, /* language=CSS */ `
	#lines {
		gap: .5em;
		padding: .5em;
	}

	.inSel {
		background-color: transparent;
		font-weight: bold;
	}

	:host(:focus) .inSel {
		background-color: transparent;
		font-weight: bold;
		outline: var(--focus-outline);
		outline-offset: -2px;
	}

	.line {
		min-height: 8em;
		max-height: 0;
		/*background-color: var(--alt1-bgcolor);*/
		/*border: 1px solid var(--border-color);*/
		border-radius: 5px;
		box-shadow: 0 2px 5px 0 var(--border-color);
		cursor: pointer;
	}

	.line:hover {
		background-color: var(--row-inSel-unfocus-bgcolor);
	}

	.cell {
		display: flex;
		flex-direction: column;
	}

  .preview {
	  flex: 1;
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex-direction: column;
	  align-items: center;
	  justify-content: center;
	  background-position: center;
	  background-repeat: no-repeat;
	  background-size: auto;
	  background-color: var(--alt1-bgcolor);
	  margin: .3em;
	  border-radius: 2px;
  }

  .name {
	  text-align: center;
	  justify-content: flex-end;
	  display: flex;
	  white-space: break-spaces;
	  height: 2.3em;
	  flex-direction: column;
	  overflow: hidden;
	  align-self: center;
	  padding: .1em .3em .1em .3em;
  }
`);


export interface JNodePropsPointer {
	_nodeProps?: JNodeShortProps
}

/**
 * function commune pour redessiner les lignes en fonction du contexte.
 */
export function redrawResLine(reg: IReg<IDepotUiEnv>, row: GridDataRowJson<JNodeShortProps>, line: HTMLElement, currentResPath: resPath | null): void {
	const path = row.cacheHolder.livePath ?? (row.cacheHolder.livePath = reg.env.resTypes.getResTypeFor(row.rowKey).livePath(row.getData('permaPath')));
	const classList = line.classList;
	classList.toggle('current', path === currentResPath && currentResPath !== null);
	classList.toggle('trashed', row.getData('trashed') === true);
	classList.toggle('unlisted', row.getData('unlisted') === true);
	// if (reg.env.lifecycle !== null) {
	// 	//liveCycle sur les res.
	// 	const lc = (row.getData<ENodeInfo>('metas') as JPersistMetas).lc;
	// 	DOM.setStyle(line, "color", lc != null ? reg.env.lifecycle.getState(lc).color || "" : "");
	// }
}

